import { injectable } from 'inversify'

/**
 * One channel per **surface** — per place feedback is shown — not one per
 * feature that raises it.
 *
 * A feature with only one surface can get away with reusing its own name as
 * the scope; a feature with several (a list plus an add-form, a page plus a
 * detail pane) needs one channel per surface, or a success on one silently
 * erases an error on the other. Naming is `feature.surface`, so a page that
 * later splits into sub-components with their own sub-presenters already has
 * a channel per sub-presenter — see `admin.inspector`/`admin.access` below
 * for why sharing one was the wrong call once they became two routes.
 *
 * Only the core/admin scopes live here — a feature adds its own entries
 * (and the matching `optionsFor` case below) when it starts writing
 * feedback.
 */
export const FeedbackScopes = [
  'app',
  'auth.form',
  // A bad join code or a create/join failure must stay put on the lobby form,
  // same reasoning as 'auth.form'.
  'lobby.form',
  // The board is the only surface in the setup phase that raises anything.
  'game.setup',
  // Same shape, for the play phase's own board — an illegal-target or
  // not-your-turn rejection.
  'game.play',
  'admin.audit',
  'admin.errors',
  'admin.users',
  // Read-only drawer surface — a load failure has nowhere else to show
  // while the drawer is open.
  'inbox',
  // The Infra Demo's own inline surface — the live consumer of show().
  'demo.feedback',
] as const
export type FeedbackScope = (typeof FeedbackScopes)[number]

/**
 * Severity vocabulary shared with notifications — same four words for the same
 * reason, so they are declared once here and aliased there rather than drifting
 * as two lookalike unions.
 *
 * `success` and `error` are **derived** from an action result and can never be
 * chosen (see FeedbackPresenter.unpackRepositoryPmToVm). `info` and `warning`
 * are only reachable through the explicit `show()` API, because nothing derives
 * them: an action either worked or it did not.
 */
export const MessageTones = ['success', 'error', 'warning', 'info'] as const
export type MessageTone = (typeof MessageTones)[number]

export type FeedbackScopeOptions = {
  /**
   * ms before a message the user does not have to act on clears itself, or null
   * to keep it until dismissed or replaced. Confirmations are transient by
   * nature — a success message has done its job once it has been seen. `info`
   * follows this one too.
   */
  successAutoDismissMs: number | null

  /**
   * Same, for messages that need the user. Almost always null: an error
   * explains why the thing next to it is empty or wrong, and a timer is not
   * evidence the user has read it. Split from the timer above because one
   * duration cannot serve both — a failed write disappearing after four
   * seconds is the exact failure mode an inline surface exists to prevent.
   *
   * `warning` follows this one: the split is about whether the user must act,
   * not about which colour the card is.
   */
  errorAutoDismissMs: number | null

  /**
   * Whether navigating away clears the channel. Almost always true — a message
   * about the page you just left is noise on the next one.
   */
  clearOnRouteChange: boolean
}

@injectable()
export class FeedbackConfig {
  readonly scopes: Record<FeedbackScope, FeedbackScopeOptions> = {
    // Shell-level surface, rendered in the fixed top-right stack: successes
    // behave like toasts, errors wait to be dismissed.
    app: { successAutoDismissMs: 5_000, errorAutoDismissMs: null, clearOnRouteChange: true },

    // A credentials failure must survive while the user fixes the form.
    'auth.form': { successAutoDismissMs: null, errorAutoDismissMs: null, clearOnRouteChange: true },

    // A rejected join code or a create/join failure must survive while the
    // player fixes it, same reasoning as 'auth.form'.
    'lobby.form': { successAutoDismissMs: null, errorAutoDismissMs: null, clearOnRouteChange: true },

    // A rejected placement must survive while the player moves the tile; the
    // "your side is set" confirmation has done its job once seen.
    'game.setup': { successAutoDismissMs: 4_000, errorAutoDismissMs: null, clearOnRouteChange: true },

    // Only ever raises an error (an illegal-target or not-your-turn
    // rejection) — `PlayBoardPresenter` never reports success on this
    // channel, so `successAutoDismissMs` is moot.
    'game.play': { successAutoDismissMs: 4_000, errorAutoDismissMs: null, clearOnRouteChange: true },

    // Read-only page: the only thing it ever reports is a failed load, which
    // must stay until it is read rather than toasting away.
    'admin.audit': { successAutoDismissMs: null, errorAutoDismissMs: null, clearOnRouteChange: true },
    'admin.errors': { successAutoDismissMs: null, errorAutoDismissMs: null, clearOnRouteChange: true },

    // Mutation confirmations ("role updated") behave like toasts; failures
    // (409 self-change, 404, forbidden) wait to be read.
    'admin.users': { successAutoDismissMs: 4_000, errorAutoDismissMs: null, clearOnRouteChange: true },

    // Read-only drawer: the only thing it ever reports is a failed poll,
    // which stays until the drawer is closed rather than toasting away.
    inbox: { successAutoDismissMs: null, errorAutoDismissMs: null, clearOnRouteChange: true },

    // The Infra Demo's own inline surface — the live consumer of show().
    'demo.feedback': {
      successAutoDismissMs: null,
      errorAutoDismissMs: null,
      clearOnRouteChange: true,
    },
  }

  optionsFor(scope: FeedbackScope): FeedbackScopeOptions {
    return this.scopes[scope]
  }

  autoDismissMsFor(scope: FeedbackScope, tone: MessageTone): number | null {
    const options = this.optionsFor(scope)
    const needsUser = tone === 'error' || tone === 'warning'
    return needsUser ? options.errorAutoDismissMs : options.successAutoDismissMs
  }

  scopesClearedOnRouteChange(): FeedbackScope[] {
    return FeedbackScopes.filter((scope) => this.scopes[scope].clearOnRouteChange)
  }
}
